/**
 * @file RagProgressPanel.jsx
 * @description Panel showing overall RAG job progress with folder cards.
 * Displays master job status, overall progress, and individual folder jobs.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Box,
  Typography,
  LinearProgress,
  Collapse,
  IconButton,
  Alert,
  Divider,
  Button,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Cancel as CancelIcon,
  WarningAmber as WarningAmberIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import RagJobCard from './RagJobCard';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { useRagErrorCodes } from '../../hooks/useRagErrorCodes';

const RagProgressPanel = ({ activeJob = null, onClose = null, onCancel = null, cancelling = false, defaultExpanded = true, sx = {}, liveProgressDetail = null }) => {
  const { t } = useTranslation();
  // #1006 — el job llega con `error_code`, no con el mensaje: el backend ya no sirve el
  // crudo de N8N. El texto sale del catálogo `rag_error_codes` (BD), no de este fichero.
  //
  // Locale fijo 'es' a propósito: `user_message_en` existe en el catálogo, pero los
  // FALLBACK_MESSAGES del hook y las etiquetas de `RagJobCard` son solo español, así que
  // pasar `i18n.language` daría una tarjeta medio traducida. Cuando se traduzca el panel
  // entero, este es el sitio.
  const { resolveFromRaw } = useRagErrorCodes();
  const resolveRagError = React.useCallback(
    (code) => resolveFromRaw(null, 'es', code),
    [resolveFromRaw]
  );
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);

  if (!activeJob) {
    return null;
  }

  const {
    id: jobId,
    status,
    folder_jobs: folderJobs = [],
    error_code: errorCode,
    failed_files: failedFiles = 0,
    created_at: createdAt
  } = activeJob;

  // Calculate overall progress
  const completedFolders = folderJobs.filter((f) => f.status === 'completed').length;
  const failedFolders = folderJobs.filter((f) => f.status === 'failed').length;
  const processingFolders = folderJobs.filter((f) =>
    f.status === 'processing' || f.status === 'queued' || f.status === 'pending'
  ).length;
  const totalFolders = folderJobs.length;

  const overallProgress = totalFolders > 0
    ? ((completedFolders + failedFolders) / totalFolders) * 100
    : 0;

  // Determine master status
  const isCompleted = status === 'completed';
  const isCompletedWithErrors = status === 'completed_with_errors';
  const isFailed = status === 'failed' || status === 'dispatch_failed';
  const isProcessing = status === 'processing';
  const isCancelled = status === 'cancelled';
  // #1294 F1: parado por saldo, que NO es lo mismo que «lo cancelaste tú».
  const isCreditStopped = status === 'credit_exhausted';

  const handleCancelClick = () => setShowCancelConfirm(true);
  const handleCancelConfirm = async () => {
    setShowCancelConfirm(false);
    await onCancel(jobId);
  };

  // Status icon and color
  let StatusIcon = SyncIcon;
  let statusColor = theme.palette.primary.main;
  let statusText = t('rag.processingStatus');

  if (isCompleted) {
    StatusIcon = CheckCircleIcon;
    statusColor = theme.palette.success.main;
    statusText = t('rag.completedStatus');
  } else if (isCompletedWithErrors) {
    StatusIcon = WarningAmberIcon;
    statusColor = theme.palette.warning.main;
    statusText = t('rag.completedWithErrorsStatus');
  } else if (isFailed) {
    StatusIcon = ErrorIcon;
    statusColor = theme.palette.error.main;
    statusText = t('rag.errorStatus');
  } else if (isCancelled) {
    StatusIcon = WarningAmberIcon;
    statusColor = theme.palette.warning.main;
    statusText = t('rag.cancelledStatus');
  } else if (isCreditStopped) {
    StatusIcon = WarningAmberIcon;
    statusColor = theme.palette.warning.main;
    statusText = t('rag.creditExhaustedStatus');
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 400,
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
        overflow: 'hidden',
        ...sx
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // La escala grey de los presets dark está invertida (grey.100 ≈ background.elevated),
          // así que el mismo token vale para ambos modos — no reintroducir ternario con grey.900.
          bgcolor: 'grey.100',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <StatusIcon
            sx={{
              color: statusColor,
              mr: 1,
              ...(isProcessing && {
                animation: 'ragSyncSpin 1.6s linear infinite',
                '@keyframes ragSyncSpin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' }
                }
              })
            }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {statusText}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('rag.foldersProgress', { completed: completedFolders + failedFolders, total: totalFolders })}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {(isProcessing || status === 'pending') && processingFolders > 0 && onCancel && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              disabled={cancelling}
              startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
              onClick={(e) => { e.stopPropagation(); handleCancelClick(); }}
              aria-label="Cancelar vectorizacion en progreso"
            >
              {cancelling ? t('rag.cancelling') : t('rag.cancel')}
            </Button>
          )}
          <IconButton size="small" onClick={(e) => e.stopPropagation()}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          {onClose && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              aria-label={t('rag.closePanelAria')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Progress bar */}
      {isProcessing && (
        overallProgress > 0 ? (
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{ height: 4 }}
          />
        ) : (
          <LinearProgress sx={{ height: 4 }} />
        )
      )}

      {/* Content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            maxHeight: 'calc(70vh - 100px)',
            overflowY: 'auto',
            p: 2
          }}
        >
          {/* Error alert */}
          {isFailed && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resolveRagError(errorCode).message}
            </Alert>
          )}

          {/* Success alert */}
          {isCompleted && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('rag.allFoldersSuccess')}
            </Alert>
          )}

          {/* Partial-failure alert */}
          {isCompletedWithErrors && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('rag.someFilesFailed', { count: failedFiles })}
            </Alert>
          )}

          {/* Cancelled alert */}
          {isCancelled && (
            <Alert severity="warning" variant="outlined" role="status" sx={{ mb: 2 }}>
              Vectorización cancelada
              {activeJob.total_folders > 0 && (
                <> — {activeJob.completed_folders} de {activeJob.total_folders} carpetas
                {activeJob.processed_files > 0 && ` (${activeJob.processed_files} archivos)`}</>
              )}
            </Alert>
          )}

          {/* Summary stats */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('rag.summary')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              {completedFolders > 0 && (
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  {t('rag.completed', { count: completedFolders })}
                </Typography>
              )}
              {processingFolders > 0 && (
                <Typography variant="body2" sx={{ color: isCancelled ? 'warning.main' : 'primary.main' }}>
                  {isCancelled ? t('rag.interrupted', { count: processingFolders }) : t('rag.processing', { count: processingFolders })}
                </Typography>
              )}
              {failedFolders > 0 && (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {t('rag.failed', { count: failedFolders })}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Folder job cards */}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('rag.folders')}
          </Typography>

          {folderJobs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {t('rag.noFoldersToProcess')}
            </Typography>
          ) : (
            folderJobs.map((folderJob) => (
              <RagJobCard
                key={folderJob.id}
                folderJob={folderJob}
                theme={theme}
                masterCancelled={isCancelled || isCreditStopped}
                liveProgressDetail={folderJob.status === 'processing' ? liveProgressDetail : null}
                resolveRagError={resolveRagError}
              />
            ))
          )}

          {/* Timestamp */}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 2, textAlign: 'center' }}
          >
            {t('rag.startedAt', { date: new Date(createdAt).toLocaleString('es-ES') })}
          </Typography>
        </Box>
      </Collapse>

      <ConfirmationDialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelConfirm}
        title={t('rag.cancelDialogTitle')}
        message={t('rag.cancelDialogMessage')}
        confirmText={t('rag.cancelDialogConfirm')}
      />
    </Paper>
  );
};

RagProgressPanel.propTypes = {
  activeJob: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    folder_jobs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        folder_name: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        processed_files: PropTypes.number,
        total_files: PropTypes.number,
        error_code: PropTypes.string
      })
    ),
    error_code: PropTypes.string,
    failed_files: PropTypes.number,
    created_at: PropTypes.string.isRequired
  }),
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  cancelling: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  sx: PropTypes.object,
  liveProgressDetail: PropTypes.shape({
    stage: PropTypes.string,
    batch: PropTypes.number,
    batchTotal: PropTypes.number
  })
};

export default RagProgressPanel;
